import dayjs from "dayjs";

/**
 * The Dashboard's plain helpers (2026-10-22 redesign): date presets, money and number words, the change against the
 * period before, and the labels of roles and statuses. No React here, so all of it is tested on its own.
 */

export const ROLE_LABEL = {
  superAdmin: "Super admin",
  client: "Admin",
  operator: "Operator",
  user: "User",
  none: "",
};

const ymd = (d) => d.format("YYYY-MM-DD");

/** The date chips. Each gives { from, to } as India days ("2026-10-01"), the server's own format. */
export function buildPresets(now = dayjs()) {
  const today = dayjs(now).startOf("day");
  return [
    { key: "today", label: "Today", from: ymd(today), to: ymd(today) },
    {
      key: "yesterday",
      label: "Yesterday",
      from: ymd(today.subtract(1, "day")),
      to: ymd(today.subtract(1, "day")),
    },
    {
      key: "7d",
      label: "Last 7 days",
      from: ymd(today.subtract(6, "day")),
      to: ymd(today),
    },
    {
      key: "month",
      label: "This month",
      from: ymd(today.startOf("month")),
      to: ymd(today),
    },
    {
      key: "lastMonth",
      label: "Last month",
      from: ymd(today.subtract(1, "month").startOf("month")),
      to: ymd(today.subtract(1, "month").endOf("month")),
    },
    {
      key: "year",
      label: "This year",
      from: ymd(today.startOf("year")),
      to: ymd(today),
    },
  ];
}

export const DEFAULT_PRESET = "month";

/** "1 Oct – 22 Oct 2026" / "22 Oct 2026". */
export function rangeLabel(from, to) {
  const f = dayjs(from);
  const t = dayjs(to);
  if (!f.isValid() || !t.isValid()) return "";
  if (from === to) return t.format("D MMM YYYY");
  return `${f.format(f.year() === t.year() ? "D MMM" : "D MMM YYYY")} – ${t.format("D MMM YYYY")}`;
}

/** ₹ in Indian words: ₹950, ₹12,450, ₹1.25 L, ₹3.4 Cr (short = for cards and chart axes). */
export function inr(value, { short = false } = {}) {
  const n = Number(value) || 0;
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (short && a >= 1e7) return `${sign}₹${trim(a / 1e7)} Cr`;
  if (short && a >= 1e5) return `${sign}₹${trim(a / 1e5)} L`;
  if (short && a >= 1e3 && a < 1e5) return `${sign}₹${trim(a / 1e3)}K`;
  return `${sign}₹${a.toLocaleString("en-IN", { maximumFractionDigits: a >= 100 ? 0 : 2 })}`;
}
const trim = (x) =>
  (x >= 100 ? x.toFixed(0) : x >= 10 ? x.toFixed(1) : x.toFixed(2)).replace(
    /\.?0+$/,
    "",
  );

/** 12,450 */
export const num = (value) => (Number(value) || 0).toLocaleString("en-IN");

/**
 * The change against the period before: { pct, dir, text }.
 *   both 0 -> no change shown; before 0 -> "New" (a percent of nothing means nothing)
 */
export function changeOf(now, before) {
  const a = Number(now) || 0;
  const b = Number(before) || 0;
  if (!a && !b) return { pct: 0, dir: "flat", text: "No change" };
  if (!b) return { pct: null, dir: "up", text: "New" };
  const pct = Math.round(((a - b) / b) * 100);
  return {
    pct,
    dir: pct > 0 ? "up" : pct < 0 ? "down" : "flat",
    text: `${pct > 0 ? "+" : ""}${pct}%`,
  };
}

/** Good morning / afternoon / evening. */
export function greeting(now = new Date()) {
  const h = now.getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

/** The Policy List's statuses in words. */
export const LIST_STATUS = {
  entryPending: "Entry pending",
  verifyPending: "Verify pending",
  approvePending: "Payable pending",
  chequePending: "Cheque pending",
  approvedPolicy: "Approved",
  rejectedPolicy: "Rejected",
  Cancelled: "Cancelled",
  cancelled: "Cancelled",
};
export const listStatusLabel = (key) =>
  LIST_STATUS[key] ||
  String(key || "Unknown").replace(/([a-z])([A-Z])/g, "$1 $2");

/** The automation's states (the donut), each with its colour. */
export const AUTOMATION = [
  { key: "completed", label: "Completed", color: "#12B76A" },
  { key: "running", label: "Running", color: "#2E90FA" },
  { key: "waiting", label: "Waiting to run", color: "#98A2B3" },
  { key: "hold", label: "On hold", color: "#F79009" },
  { key: "failed", label: "Failed", color: "#F04438" },
  { key: "none", label: "Not sent", color: "#D0D5DD" },
];

/** A trend key ("2026-10-05" or "2026-10") as the axis shows it. */
export function trendLabel(key, unit) {
  const d = dayjs(unit === "month" ? `${key}-01` : key);
  if (!d.isValid()) return key;
  return unit === "month" ? d.format("MMM YY") : d.format("D MMM");
}

/** The pending-work tiles: which count, what it is called, where it opens. */
export const PENDING_TILES = [
  { key: "entry", label: "Entry pending", to: "/entryPending" },
  { key: "verify", label: "Verify pending", to: "/pendingPolicy" },
  { key: "cheque", label: "Cheque pending", to: "/chequePending" },
  { key: "payable", label: "Payable pending", to: "/userPayable" },
  { key: "receivable", label: "Receivable pending", to: "/receivaleStatus" },
];

/** Days until a date, in words: "Today", "Tomorrow", "In 5 days". */
export function dueIn(date, now = new Date()) {
  const d = dayjs(date).startOf("day");
  if (!d.isValid()) return "";
  const days = d.diff(dayjs(now).startOf("day"), "day");
  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}
